import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import LandingHero from "../components/LandingHero";

const FOREST = "#0D9488";
const FOREST_D = "#0F766E";
const INK = "#0F172A";
const INK_MUT = "#64748B";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("session_revoked") === "1") {
      toast.error("Your session was ended by an administrator. Please sign in again.", { duration: 6000 });
    }
  }, [searchParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "h-12 rounded-lg bg-white border focus-visible:ring-0 focus-visible:border-[#0D9488] transition-colors text-slate-900 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:border-slate-700";
  const inputStyle = { borderColor: "#E2E8F0" };

  return (
    <LandingHero settings={settings}>
      <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em]" style={{ color: INK_MUT }}>Email</Label>
          <Input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            style={inputStyle}
            placeholder="you@example.com"
            data-testid="login-email"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em]" style={{ color: INK_MUT }}>Password</Label>
          <Input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            style={inputStyle}
            placeholder="••••••••"
            data-testid="login-password"
          />
        </div>

        <div className="flex justify-end -mt-2">
          <Link
            to="/forgot-password"
            className="text-xs uppercase tracking-[0.2em] hover:underline"
            style={{ color: INK_MUT }}
            data-testid="link-forgot-password"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="w-full h-12 rounded-lg text-white uppercase tracking-widest text-xs font-semibold transition-colors"
          style={{ background: FOREST }}
          onMouseEnter={(e) => (e.currentTarget.style.background = FOREST_D)}
          onMouseLeave={(e) => (e.currentTarget.style.background = FOREST)}
          data-testid="login-submit"
        >
          {submitting ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>)}
        </Button>
      </form>

      <div className="mt-6 pt-5 text-sm text-center" style={{ borderTop: "1px solid #E2E8F0", color: INK_MUT }}>
        No account?{" "}
        <Link to="/signup" className="hover:underline font-semibold" style={{ color: FOREST }} data-testid="link-signup">
          Create one
        </Link>
      </div>
    </LandingHero>
  );
}
